import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import { Redux } from '../..'
import {
  MasterProduct,
  MenuContainer,
  MenuContainersWithMasterProduct,
  MenuContainerWithChildren
} from '@/src/types/apps/gygMenuContainerTypes'
import { GygListDataResponse, Meta } from '@/src/types/apps/commonTypes'
import { GygQueryOptions, MasterProductPriceListOptions } from '@/src/views/components/master-pricing-list/types'
import { stringify } from 'qs'
import { AxiosError } from 'axios'

interface MenuContainerInitialState {
  menuContainers?: MenuContainer[] | []
  childMenuContainers?: MenuContainer[] | []
  menuContainersWithMasterProducts?: MenuContainersWithMasterProduct[]
  masterProductsByContainer?: { [key: number]: MasterProduct[] }
  menuContainerWithChildren?: MenuContainerWithChildren
  meta: Meta | null
  loading: boolean
  success: string | null
  error: string | null | AxiosError
  isDialogOpen: boolean
  selectedProduct: MasterProduct | null
  masterProductsObject?: {
    products: {
      [key: number]: MasterProduct[]
    }
    modifiers: {
      [key: number]: MasterProduct[]
    }
  }
}

const initialState: MenuContainerInitialState = {
  menuContainers: [],
  childMenuContainers: [],
  menuContainersWithMasterProducts: [],
  menuContainerWithChildren: undefined,
  meta: null,
  loading: false,
  success: null,
  error: null,
  isDialogOpen: false,
  selectedProduct: null,
  masterProductsObject: { products: {}, modifiers: {} }
}

interface uploadMenuContainerImagesResponse {
  data: any
}

interface MenuContainersWithMasterProductResponse {
  data: any
}

export interface MenuContainerTagsPayload {
  menuContainerPosPlu: number
  posMenus: number[]
  tagId: string
}

export function getSanitisedParams(params: GygQueryOptions) {
  const prms = { ...params }
  if (!params.filters || Object.keys(params.filters).length === 0) {
    delete prms.filters
  } else {
    // Remove keys from prms object which have empty values
    Object.keys(params.filters).forEach(key => {
      if (!params.filters?.[key]) {
        delete params.filters?.[key]
      }
    })
  }

  return prms
}

export const getMenuContainerList = createAsyncThunk<GygListDataResponse<MenuContainer>, GygQueryOptions, Redux>(
  'gygMenuContainers/getMenuContainerList',
  async (params, { extra }) => {
    const sanitizedParams = getSanitisedParams(params)
    const urlParams = stringify(sanitizedParams, { arrayFormat: 'brackets', encode: false })

    const response = await extra.httpClient.get(`menu-containers?${urlParams}`)

    return response.data
  }
)

export const getMenuContainersWithMasterProductsList = createAsyncThunk<
  GygListDataResponse<MenuContainersWithMasterProduct>,
  MasterProductPriceListOptions,
  Redux
>('gygMenuContainers/getMenuContainersWithMasterProductsList', async (params, { extra }) => {
  const sanitizedParams = getSanitisedParams(params)
  const urlParams = stringify(sanitizedParams, { arrayFormat: 'brackets', encode: false })

  const sectionId = params.filters?.['masterProductRelationships.sectionId']

  const response = await extra.httpClient.get(`menu-containers/by-section/${sectionId}?${urlParams}`)

  return response.data
})

export const getMenuContainerWithChildren = createAsyncThunk<MenuContainersWithMasterProductResponse, number, Redux>(
  'gygMenuContainers/getMenuContainerWithChildren',
  async (id, { extra }) => {
    const response = await extra.httpClient.get(`menu-containers/${id}/with-children`)

    return response.data
  }
)

export const getChildContainersByContainer = createAsyncThunk<MenuContainersWithMasterProductResponse, any, Redux>(
  'gygMenuContainers/getChildContainers',
  async (params, { extra }) => {
    const { id } = params
    const sanitizedParams = getSanitisedParams(params)
    const urlParams = stringify(sanitizedParams, { arrayFormat: 'brackets', encode: false })

    const response = await extra.httpClient.get(`menu-containers/${id}/master-products?${urlParams}`)

    return response.data
  }
)

export const getMasterProductsByContainer = createAsyncThunk<MenuContainersWithMasterProductResponse, any, Redux>(
  'gygMenuContainers/getMasterProducts',
  async (params, { extra }) => {
    const { id } = params
    const sanitizedParams = getSanitisedParams(params)
    const urlParams = stringify(sanitizedParams, { arrayFormat: 'brackets', encode: false })

    const response = await extra.httpClient.get(`menu-containers/${id}/master-products?${urlParams}`)

    return response.data
  }
)

export const getMasterProductsByContainerBundles = createAsyncThunk<
  MenuContainersWithMasterProductResponse,
  any,
  Redux
>('gygMenuContainers/getMasterProductsBundles', async (params, { extra }) => {
  const { id } = params
  const sanitizedParams = getSanitisedParams(params)
  const urlParams = stringify(sanitizedParams, { arrayFormat: 'brackets', encode: false })

  const response = await extra.httpClient.get(`menu-containers/${id}/master-products?${urlParams}`)

  return response.data
})

export const getModifiersByContainer = createAsyncThunk<MenuContainersWithMasterProductResponse, any, Redux>(
  'gygMenuContainers/getModifiers',
  async (params, { extra }) => {
    const { id } = params
    const sanitizedParams = getSanitisedParams(params)
    const urlParams = stringify(sanitizedParams, { arrayFormat: 'brackets', encode: false })

    const response = await extra.httpClient.get(`menu-containers/${id}/modifiers?${urlParams}`)

    return response.data
  }
)

export const getModifiersByContainerBundles = createAsyncThunk<MenuContainersWithMasterProductResponse, any, Redux>(
  'gygMenuContainers/getModifierBundles',
  async (params, { extra }) => {
    const { id } = params
    const sanitizedParams = getSanitisedParams(params)
    const urlParams = stringify(sanitizedParams, { arrayFormat: 'brackets', encode: false })

    const response = await extra.httpClient.get(`menu-containers/${id}/modifiers?${urlParams}`)

    return response.data
  }
)

export const uploadMenuContainerImages = createAsyncThunk<uploadMenuContainerImagesResponse, FormData, Redux>(
  'gygMenuContainerProducts/uploadMenuContainerProductImages',
  async (payload, { extra }) => {
    const { data: responseData } = await extra.httpClient.post(`menu-containers/images`, payload)

    return responseData
  }
)

export const saveMenuContainerTags = createAsyncThunk<MenuContainerTagsPayload, MenuContainerTagsPayload[], Redux>(
  'gygMenuContainerProducts/saveMenuContainerTag',
  async (menuTags, { extra }) => {
    const { data: responseData } = await extra.httpClient.put(`menu-container-tags/bulk`, menuTags)

    return responseData
  }
)

const gygMenuContainerListSlice = createSlice({
  name: 'gygMenuContainers',
  initialState,
  reducers: {
    setDialogOpen: (state, action) => {
      state.isDialogOpen = action.payload
    },
    setSelectedProduct: (state, action) => {
      state.selectedProduct = action.payload
    }
  },
  extraReducers: builder => {
    builder
      .addCase(getMenuContainerList.pending, state => {
        state.loading = true
        state.error = null
      })
      .addCase(getMenuContainerList.fulfilled, (state, action) => {
        state.loading = false
        state.success = null
        state.menuContainers = action.payload.data
        state.meta = action.payload.meta
      })
      .addCase(getMenuContainerList.rejected, (state, action) => {
        state.loading = false
        state.meta = null
        state.error = action.error.message ?? 'Failed to fetch menu containers list'
      })
      .addCase(getChildContainersByContainer.pending, state => {
        state.loading = true
        state.error = null
      })
      .addCase(getChildContainersByContainer.fulfilled, (state, action) => {
        state.loading = false
        state.success = null
        state.childMenuContainers = action.payload.data
      })
      .addCase(getChildContainersByContainer.rejected, (state, action) => {
        state.loading = false
        state.meta = null
        state.error = action.error.message ?? 'Failed to fetch menu containers list'
      })
      .addCase(getMenuContainersWithMasterProductsList.pending, state => {
        state.loading = true
        state.error = null
      })
      .addCase(getMenuContainersWithMasterProductsList.fulfilled, (state, action) => {
        state.loading = false
        state.success = null
        state.menuContainersWithMasterProducts = action.payload.data
        state.meta = action.payload.meta
      })
      .addCase(getMenuContainersWithMasterProductsList.rejected, (state, action) => {
        state.loading = false
        state.meta = null
        state.error = action.error.message ?? 'Failed to fetch menu containers list'
      })
      .addCase(getMenuContainerWithChildren.pending, state => {
        state.loading = true
        state.error = null
      })
      .addCase(getMenuContainerWithChildren.fulfilled, (state, action) => {
        state.loading = false
        state.success = null
        state.menuContainerWithChildren = action.payload.data
      })
      .addCase(getMenuContainerWithChildren.rejected, (state, action) => {
        state.loading = false
        state.error = action.error.message ?? 'Failed to fetch menu containers list'
      })
      .addCase(getMasterProductsByContainer.pending, state => {
        state.loading = true
        state.error = null
      })
      .addCase(getMasterProductsByContainer.fulfilled, (state, action) => {
        state.loading = false
        state.success = null

        if (
          Array.isArray(state.menuContainersWithMasterProducts) &&
          Array.isArray(action.payload?.data) &&
          action.payload.data.length > 0
        ) {
          const menuContainerId = action.payload.data[0].menuContainerId

          const updatedContainers = state.menuContainersWithMasterProducts.map(container => {
            if (container.id === menuContainerId) {
              return { ...container, masterProducts: action.payload.data }
            }

            return container
          })

          state.menuContainersWithMasterProducts = updatedContainers
        } else {
          console.info('getMasterProductsByContainer.fulfilled: Invalid state or payload data.')
        }
      })
      .addCase(getModifiersByContainer.rejected, (state, action) => {
        state.loading = false
        state.error = action.error.message ?? 'Failed to fetch menu containers list'
      })
      .addCase(getModifiersByContainer.pending, state => {
        state.loading = true
        state.error = null
      })
      .addCase(getModifiersByContainer.fulfilled, (state, action) => {
        state.loading = false
        state.success = null

        if (
          Array.isArray(state.menuContainersWithMasterProducts) &&
          Array.isArray(action.payload?.data) &&
          action.payload.data.length > 0
        ) {
          const menuContainerId = action.payload.data[0].menuContainerId

          const updatedContainers = state.menuContainersWithMasterProducts.map(container => {
            if (container.id === menuContainerId) {
              return { ...container, modifiers: action.payload.data }
            }

            return container
          })

          state.menuContainersWithMasterProducts = updatedContainers
        } else {
          console.info('getMasterProductsByContainer.fulfilled: Invalid state or payload data.')
        }
      })

      .addCase(getMasterProductsByContainerBundles.pending, state => {
        state.loading = true
        state.error = null
      })
      .addCase(getMasterProductsByContainerBundles.fulfilled, (state, action) => {
        state.loading = false
        state.success = null
        state.masterProductsObject = state.masterProductsObject || { products: {}, modifiers: {} }
        state.masterProductsObject.products = {
          ...state.masterProductsObject?.products,
          [action.payload.data?.[0]?.menuContainerId]: action.payload.data
        }
      })
      .addCase(getMasterProductsByContainerBundles.rejected, (state, action) => {
        state.loading = false
        state.error = action.error.message ?? 'Failed to fetch menu containers list'
      })
      .addCase(getModifiersByContainerBundles.rejected, (state, action) => {
        state.loading = false
        state.error = action.error.message ?? 'Failed to fetch menu containers list'
      })
      .addCase(getModifiersByContainerBundles.pending, state => {
        state.loading = true
        state.error = null
      })
      .addCase(getModifiersByContainerBundles.fulfilled, (state, action) => {
        state.loading = false
        state.success = null

        state.masterProductsObject = state.masterProductsObject || { products: {}, modifiers: {} }
        state.masterProductsObject.modifiers = {
          ...state.masterProductsObject?.modifiers,
          [action.payload.data?.[0]?.menuContainerId]: action.payload.data
        }
      })

      .addCase(uploadMenuContainerImages.pending, state => {
        state.loading = true
        state.error = null
        state.success = null
      })
      .addCase(uploadMenuContainerImages.fulfilled, state => {
        state.loading = false
        state.success = 'uploaded'
      })
      .addCase(uploadMenuContainerImages.rejected, (state, action) => {
        state.loading = false
        state.success = null
        state.error = action.error.message ?? 'Failed to upload images'
      })
      .addCase(saveMenuContainerTags.pending, state => {
        state.loading = true
        state.error = null
        state.success = null
      })
      .addCase(saveMenuContainerTags.fulfilled, state => {
        state.loading = false
        state.success = 'saved'
      })
      .addCase(saveMenuContainerTags.rejected, (state, action) => {
        state.loading = false
        state.success = null
        state.error = action.error.message ?? 'Failed to save tags'
      })
  }
})
export const { setDialogOpen, setSelectedProduct } = gygMenuContainerListSlice.actions

export default gygMenuContainerListSlice.reducer
